import * as DialogPrimitive from "@radix-ui/react-dialog";
import { FocusScope } from "@radix-ui/react-focus-scope";
import { Button } from "../components/Button";
import { ScrollArea } from "../components/ScrollArea";
import { Separator } from "../components/Separator";
import { XClose } from "../icons/v2-stroke";
import { cn } from "../lib/utils";
import { cva } from "class-variance-authority";
import * as React from "react";
const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogClose = DialogPrimitive.Close;
const DialogPortal = DialogPrimitive.Portal;
const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (React.createElement(DialogPrimitive.Overlay, { className: cn("fixed inset-0 z-50 duration-200 ease-emphasized data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:duration-150 motion-reduce:animate-none", "bg-muted-foreground/75 dark:bg-muted-background/75", className), ...props, ref: ref })));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;
const DIALOG_SIZES = ["md", "lg", "xl", "2xl", "full", "fit"];
const DIALOG_HEIGHTS = ["md", "lg", "xl", "2xl"];
const sizeClasses = {
    md: "sm:max-w-md",
    lg: "sm:max-w-xl",
    xl: "sm:max-w-3xl",
    "2xl": "sm:max-w-5xl",
    full: "sm:max-w-full sm:h-full",
    fit: "sm:max-w-[90vw] w-fit",
};
const heightClasses = {
    md: "sm:h-md",
    lg: "sm:h-lg",
    xl: "sm:h-xl",
    "2xl": "sm:h-2xl",
};
const DIALOG_VARIANTS = ["default", "command"];
const variantClasses = {
    default: cn("top-[50%] translate-y-[-50%] duration-200 ease-emphasized data-[state=closed]:duration-150 motion-reduce:animate-none", "data-[state=open]:animate-in data-[state=closed]:animate-out", "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0", "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"),
    command: cn("top-[20%] duration-200 ease-emphasized data-[state=closed]:duration-150 motion-reduce:animate-none", "data-[state=open]:animate-in data-[state=closed]:animate-out", "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0", "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95", "data-[state=open]:slide-in-from-top-2 data-[state=closed]:slide-out-to-top-2"),
};
const dialogVariants = cva(cn("fixed left-[50%] z-50 overflow-hidden translate-x-[-50%]", "rounded-2xl flex flex-col w-full max-w-[calc(100vw-2rem)] border border shadow-lg", "bg-modal-background", "border-border", "max-h-[90vh]"), {
    variants: {
        size: sizeClasses,
        height: heightClasses,
        variant: variantClasses,
    },
    defaultVariants: {
        size: "md",
        variant: "default",
    },
});
const DialogContent = React.forwardRef(({ className, children, size, height, variant, trapFocusScope, isAlertDialog, preventAutoFocusOnClose = true, onCloseAutoFocus, mountPortalContainer, ...props }, ref) => {
    const handleCloseAutoFocus = React.useCallback((event) => {
        if (preventAutoFocusOnClose) {
            event.preventDefault();
        }
        onCloseAutoFocus === null || onCloseAutoFocus === void 0 ? void 0 : onCloseAutoFocus(event);
    }, [preventAutoFocusOnClose, onCloseAutoFocus]);
    return (React.createElement(DialogPortal, { container: mountPortalContainer },
        React.createElement(DialogOverlay, null),
        React.createElement(FocusScope, { trapped: trapFocusScope, asChild: true },
            React.createElement(DialogPrimitive.Content, { ref: ref, className: cn(dialogVariants({ size, height, variant }), className), onInteractOutside: isAlertDialog
                    ? (e) => e.preventDefault()
                    : props.onInteractOutside, onCloseAutoFocus: handleCloseAutoFocus, ...props }, children))));
});
DialogContent.displayName = DialogPrimitive.Content.displayName;
const DialogHeader = ({ className, children, buttonSize = "mini", buttonVariant = "ghost", hideButton = false, ...props }) => (React.createElement("div", { className: cn("sticky top-0 z-50 flex flex-none flex-col gap-0 bg-modal-background px-5 pt-4 text-left", className), ...props },
    children,
    !hideButton && (React.createElement(DialogClose, { asChild: true, className: "absolute right-3 top-3" },
        React.createElement(Button, { icon: XClose, variant: buttonVariant, size: buttonSize })))));
DialogHeader.displayName = "DialogHeader";
const DialogContainer = ({ children, fixedContent, className, }) => {
    const contentStyles = cn("copy-base break-words px-5 py-4 text-foreground");
    const scrollableContent = (React.createElement(ScrollArea, { className: "w-full flex-grow" },
        React.createElement("div", { className: cn(contentStyles, "relative flex flex-col gap-2 text-left", className) }, children)));
    if (fixedContent) {
        return (React.createElement("div", { className: "flex flex-grow flex-col overflow-hidden" },
            React.createElement("div", { className: cn(contentStyles, "flex-none") }, fixedContent),
            React.createElement(Separator, null),
            scrollableContent));
    }
    return scrollableContent;
};
DialogContainer.displayName = "DialogContainer";
const DialogFooter = ({ className, children, leftButtonProps, rightButtonProps, dialogCloseClassName, ...props }) => (React.createElement("div", { className: "flex flex-none flex-col gap-0" },
    React.createElement("div", { className: cn("flex flex-none flex-row justify-end gap-2 px-3 pb-3 pt-2", className), ...props },
        leftButtonProps &&
            (leftButtonProps.disabled ? (React.createElement(Button, { ...leftButtonProps })) : (React.createElement(DialogClose, { className: dialogCloseClassName, asChild: true },
                React.createElement(Button, { ...leftButtonProps })))),
        rightButtonProps &&
            (rightButtonProps.disabled ? (React.createElement(Button, { ...rightButtonProps })) : (React.createElement(DialogClose, { className: dialogCloseClassName, asChild: true },
                React.createElement(Button, { ...rightButtonProps })))),
        children)));
DialogFooter.displayName = "DialogFooter";
const DialogTitle = React.forwardRef(({ className, visual, children, ...props }, ref) => (React.createElement("div", { className: "flex flex-row items-center gap-2 pt-1" },
    visual,
    React.createElement(DialogPrimitive.Title, { ref: ref, className: cn("heading-lg", "min-w-0 break-words", "text-foreground", className), ...props }, children))));
DialogTitle.displayName = DialogPrimitive.Title.displayName;
const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (React.createElement(DialogPrimitive.Description, { ref: ref, className: cn("copy-sm", "text-muted-foreground", className), ...props })));
DialogDescription.displayName = DialogPrimitive.Description.displayName;
export { Dialog, DialogClose, DialogContainer, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogOverlay, DialogPortal, DialogTitle, DialogTrigger, };
//# sourceMappingURL=Dialog.js.map